import { SearchSuggestion, SearchSuggestionGroup } from './search-suggestions.model';

export const SUGGESTIONS_PER_GROUP = 4;

// Live/upcoming events and their recordings; parent and child streams share the event's slug.
const EVENT_TYPES = ['stream', 'recvideo', 'recvideoalbum'];
const MEDIA_TYPES = ['audio', 'audioalbum', 'video', 'videoalbum'];

/** Same event page the search results page opens. */
function eventSuggestion(doc: any, base: string, fallbackPic: string): SearchSuggestion {
  return { kind: 'event', title: doc.name, pic: doc.pic || fallbackPic, path: `${base}/event/${doc.slug}` };
}

/** Same album page (and back link) IcSearchComponent.goToAlbum opens. */
function mediaSuggestion(doc: any, base: string, fallbackPic: string): SearchSuggestion {
  const isAlbum = doc.type === 'audioalbum' || doc.type === 'videoalbum';
  const albumType = doc.type === 'audioalbum' ? 'audio' : doc.type === 'videoalbum' ? 'video' : doc.type;
  return {
    kind: 'media',
    title: isAlbum ? doc.name : (doc.stage || doc.name),
    pic: doc.pic || fallbackPic,
    path: `${base}/album/${albumType}/${doc.slug}`,
    queryParams: isAlbum ? undefined : { id: doc.id },
    backHistory: `${base}/album/${albumType}/${doc.id}`,
  };
}

/** Same collection page the network Merchandise page opens. */
function merchSuggestion(doc: any, base: string): SearchSuggestion {
  return {
    kind: 'merch',
    title: doc.title,
    pic: doc.logo && doc.logo !== 'NA' ? doc.logo : doc.images,
    path: `${base}/merchandise/product-marketplace/${doc.collection_id}`,
  };
}

function firstUnique(items: SearchSuggestion[]): SearchSuggestion[] {
  const seen = new Set<string>();
  return items.filter((item) => !seen.has(item.path) && !!seen.add(item.path)).slice(0, SUGGESTIONS_PER_GROUP);
}

/** Groups in panel order; a source that failed (null) or found nothing leaves its group out. */
export function groupSuggestions(content: any[] | null, merch: any[] | null, base: string, fallbackPic: string): SearchSuggestionGroup[] {
  const docs = (content || []).filter((doc) => doc && doc.slug);
  const groups: SearchSuggestionGroup[] = [
    { label: 'Events', items: firstUnique(docs.filter((doc) => EVENT_TYPES.includes(doc.type)).map((doc) => eventSuggestion(doc, base, fallbackPic))) },
    { label: 'Videos & Audio', items: firstUnique(docs.filter((doc) => MEDIA_TYPES.includes(doc.type)).map((doc) => mediaSuggestion(doc, base, fallbackPic))) },
    { label: 'Merchandise', items: firstUnique((merch || []).filter((doc) => doc && doc.collection_id && doc.title).map((doc) => merchSuggestion(doc, base))) },
  ];
  return groups.filter((group) => group.items.length);
}

const DETAIL_SEGMENTS = ['event', 'album', 'merchandise'];

function detailSegmentOf(url: string): string {
  const segments = (url || '').split(/[?#]/)[0].split('/').filter(Boolean);
  return segments.find((segment) => DETAIL_SEGMENTS.includes(segment)) || '';
}

/**
 * True when the suggestion opens the same kind of detail page the fan is on. Those pages read their
 * slug once on init, so a router navigation would keep the old item on screen (see isEventDetailUrl).
 */
export function opensSameDetailPage(currentUrl: string, path: string): boolean {
  const current = detailSegmentOf(currentUrl);
  return !!current && current === detailSegmentOf(path);
}
